/**
 * Password validation utilities
 * Ensures consistency with backend validation requirements
 */

export interface PasswordStrength {
  score: number;
  feedback: string[];
  isValid: boolean;
}

/**
 * Calculate password strength based on backend requirements
 * Must match backend validation in be/src/modules/auth/auth.service.ts
 */
export const calculatePasswordStrength = (
  password: string,
): PasswordStrength => {
  const feedback: string[] = [];
  let score = 0;

  if (password.length === 0) {
    return { score: 0, feedback: [], isValid: false };
  }

  // Length check (minimum 8 characters)
  if (password.length >= 8) {
    score += 1;
  } else {
    feedback.push('At least 8 characters');
  }

  // Lowercase check
  if (/[a-z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('At least one lowercase letter');
  }

  // Uppercase check
  if (/[A-Z]/.test(password)) {
    score += 1;
  } else {
    feedback.push('At least one uppercase letter');
  }

  // Number check
  if (/\d/.test(password)) {
    score += 1;
  } else {
    feedback.push('At least one number');
  }

  // Special character check - matches backend regex exactly
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    score += 1;
  } else {
    feedback.push('At least one special character');
  }

  // Password is valid if it meets all 5 requirements
  const isValid = score >= 5 && password.length >= 8;

  return { score, feedback, isValid };
};

/**
 * Validate password and return error message if invalid
 */
export const validatePassword = (password: string): string | undefined => {
  if (!password) {
    return 'Password is required';
  }

  const strength = calculatePasswordStrength(password);
  if (!strength.isValid) {
    return 'Password does not meet security requirements';
  }

  return undefined;
};

/**
 * Validate password with detailed requirements message
 */
export const validatePasswordDetailed = (
  password: string,
): string | undefined => {
  if (!password) {
    return 'Password is required';
  }

  if (password.length < 8) {
    return 'Password must be at least 8 characters';
  }

  // Check for mixed case, numbers, and special characters
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

  if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
    return 'Password must contain uppercase, lowercase, numbers, and special characters';
  }

  return undefined;
};
